// Deterministic heightfield for the battlefield. Purely visual — gameplay is
// planar — but every placed object and character samples heightAt() so the
// world reads as rolling terrain. Flat zones are carved out where gameplay
// needs readability: castles, the village, and (mildly) the battle corridor.

import { BRIDGE, RIVER, VILLAGE_CENTER } from '../sim/world';

function hash2(ix: number, iz: number): number {
  let h = ix * 374761393 + iz * 668265263;
  h = (h ^ (h >> 13)) * 1274126177;
  return (((h ^ (h >> 16)) >>> 0) % 100000) / 100000;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, z: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = smooth(x - ix);
  const fz = smooth(z - iz);
  const a = hash2(ix, iz);
  const b = hash2(ix + 1, iz);
  const c = hash2(ix, iz + 1);
  const d = hash2(ix + 1, iz + 1);
  return (a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

export function heightAt(x: number, z: number): number {
  // rolling base: two octaves
  let h = (valueNoise(x / 60 + 31, z / 60 + 17) - 0.5) * 2 * 3.4;
  h += (valueNoise(x / 24 + 7, z / 24 + 91) - 0.5) * 2 * 1.2;

  // feature hills: a broad NW hill and a SE ridge
  h += 6 * Math.exp(-((x + 85) ** 2 / 2800 + (z - 82) ** 2 / 2600));
  h += 5 * Math.exp(-((x - 85) ** 2 / 2600 + (z + 68) ** 2 / 1400));

  // flatten toward the castles (gameplay zones stay level; the enlarged
  // castles start at |x| 162 with towers reaching |x| 158)
  h *= clamp01((146 - Math.abs(x)) / 26);
  // flatten the village
  const dv = Math.hypot(x - VILLAGE_CENTER.x, z - VILLAGE_CENTER.z);
  h *= clamp01((dv - 22) / 16) * 0.9 + 0.1 * clamp01((dv - 22) / 16);
  // soften the central battle corridor so formations read clearly
  h *= 0.45 + 0.55 * clamp01((Math.abs(z) - 24) / 36);

  // river carve (skip the bridge crossing so the deck sits level)
  const inBridge = z > BRIDGE.minZ && z < BRIDGE.maxZ;
  if (!inBridge && z > RIVER.minZ - 6) {
    const cx = (RIVER.minX + RIVER.maxX) / 2;
    const half = (RIVER.maxX - RIVER.minX) / 2;
    const t = clamp01(1 - (Math.abs(x - cx) - half * 0.4) / (half * 1.4));
    const north = clamp01((z - (RIVER.minZ - 6)) / 10);
    h -= 2.2 * smooth(t) * north;
  }

  return h;
}
